// src/components/payroll/settings/PayrollSettings.js
import React, { useState, useEffect, useContext } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import {
  collection, query, where, getDocs, doc, setDoc,
  updateDoc, serverTimestamp
} from 'firebase/firestore';
import {
  AlertCircle, Save, Check, Settings, X
} from 'lucide-react';

const PayrollSettings = () => {
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [config, setConfig] = useState({
    payPeriod: 'monthly',
    taxSettings: {
      employerContributionRate: 10, // Stored as percentage (10%)
      taxCalculationMethod: 'standard'
    },
    autoPaymentEnabled: false,
    defaultDeductions: [],
    defaultBenefits: []
  });
  
  // New deduction/benefit form
  const [newItem, setNewItem] = useState({
    type: 'deduction',
    name: '',
    value: '',
    valueType: 'percentage',
    description: ''
  });
  
  // Fetch existing configuration
  useEffect(() => {
    const fetchConfig = async () => {
      if (!currentUser || !activeBusiness) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const configQuery = query(
          collection(db, 'payrollConfigurations'),
          where('businessId', '==', activeBusiness.id)
        );
        
        const configSnapshot = await getDocs(configQuery);
        
        if (!configSnapshot.empty) {
          const configData = configSnapshot.docs[0].data();
          
          // Convert employer contribution rate from decimal to percentage for display
          const displayData = {
            ...configData,
            taxSettings: {
              ...configData.taxSettings,
              employerContributionRate: (configData.taxSettings?.employerContributionRate || 0) * 100
            }
          };
          
          setConfig(displayData);
        }
        
      } catch (err) {
        console.error('Error fetching payroll configuration:', err);
        setError('Failed to load configuration. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchConfig();
  }, [currentUser, activeBusiness]);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('taxSettings.')) {
      const taxField = name.split('.')[1];
      setConfig(prev => ({
        ...prev,
        taxSettings: {
          ...prev.taxSettings,
          [taxField]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear success message when form is changed
    if (success) setSuccess(false);
  };
  
  // Handle new item input changes
  const handleNewItemChange = (e) => {
    const { name, value } = e.target;
    setNewItem(prev => ({ ...prev, [name]: value }));
  };
  
  // Add new deduction/benefit
  const handleAddItem = () => {
    if (!newItem.name || !newItem.value) return;
    
    const newItemObj = {
      name: newItem.name,
      type: newItem.valueType,
      value: parseFloat(newItem.value),
      description: newItem.description
    };
    
    if (newItem.type === 'deduction') {
      setConfig(prev => ({
        ...prev,
        defaultDeductions: [...prev.defaultDeductions, newItemObj]
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        defaultBenefits: [...prev.defaultBenefits, newItemObj]
      }));
    }
    
    // Reset form
    setNewItem({
      type: 'deduction',
      name: '',
      value: '',
      valueType: 'percentage',
      description: ''
    });
  };
  
  // Remove deduction/benefit
  const handleRemoveItem = (type, index) => {
    if (type === 'deduction') {
      setConfig(prev => ({
        ...prev,
        defaultDeductions: prev.defaultDeductions.filter((_, i) => i !== index)
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        defaultBenefits: prev.defaultBenefits.filter((_, i) => i !== index)
      }));
    }
  };
  
  // Save configuration
  const handleSave = async () => {
    if (!currentUser || !activeBusiness) return;
    
    try {
      setSaving(true);
      setError(null);
      
      // Convert percentage back to decimal for storage
      const saveData = {
        ...config,
        businessId: activeBusiness.id,
        taxSettings: {
          ...config.taxSettings,
          employerContributionRate: parseFloat(config.taxSettings.employerContributionRate) / 100
        },
        updatedAt: serverTimestamp()
      };
      
      // Check if config already exists
      const configQuery = query(
        collection(db, 'payrollConfigurations'),
        where('businessId', '==', activeBusiness.id)
      );
      
      const configSnapshot = await getDocs(configQuery);
      
      if (configSnapshot.empty) {
        // Create new configuration
        saveData.createdAt = serverTimestamp();
        await setDoc(doc(collection(db, 'payrollConfigurations')), saveData);
      } else {
        // Update existing configuration
        await updateDoc(doc(db, 'payrollConfigurations', configSnapshot.docs[0].id), saveData);
      }
      
      setSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
      
    } catch (err) {
      console.error('Error saving payroll configuration:', err);
      setError('Failed to save configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 flex justify-center items-center min-h-[300px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h1 className="text-xl font-bold text-gray-900 flex items-center">
          <Settings size={20} className="mr-2 text-[#190192]" />
          Payroll Settings
        </h1>
        <p className="text-gray-500">
          Configure tax rates, deductions, and other payroll settings
        </p>
      </div>
      
      <div className="p-6">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 rounded-lg text-green-700 flex items-center">
            <Check size={20} className="mr-2" />
            <span>Settings saved successfully!</span>
          </div>
        )}
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 rounded-lg text-red-700 flex items-center">
            <AlertCircle size={20} className="mr-2" />
            <span>{error}</span>
          </div>
        )}
        
        <div className="space-y-8">
          {/* General Payroll Settings */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">General Settings</h2>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="payPeriod" className="block text-sm font-medium text-gray-700 mb-1">
                    Default Pay Period
                  </label>
                  <select
                    id="payPeriod"
                    name="payPeriod"
                    value={config.payPeriod}
                    onChange={handleInputChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </div>
                
                <div>
                  <div className="flex items-center h-full">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="autoPaymentEnabled"
                        checked={config.autoPaymentEnabled}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-[#190192] focus:ring-[#190192] border-gray-300 rounded"
                      />
                      <span className="ml-2 text-gray-700">Enable Automatic Payments</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Tax Settings */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Tax Settings</h2>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="employerContributionRate" className="block text-sm font-medium text-gray-700 mb-1">
                    Employer Contribution Rate (%)
                  </label>
                  <input
                    type="number"
                    id="employerContributionRate"
                    name="taxSettings.employerContributionRate"
                    value={config.taxSettings.employerContributionRate}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    The percentage of employee salaries your business contributes to benefits
                  </p>
                </div>
                
                <div>
                  <label htmlFor="taxCalculationMethod" className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Calculation Method
                  </label>
                  <select
                    id="taxCalculationMethod"
                    name="taxSettings.taxCalculationMethod"
                    value={config.taxSettings.taxCalculationMethod}
                    onChange={handleInputChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                  >
                    <option value="standard">Standard Progressive Tax</option>
                    <option value="flat">Flat Rate Tax</option>
                    <option value="custom">Custom Tax Brackets</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          {/* Default Deductions & Benefits */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Default Deductions & Benefits</h2>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="md:col-span-1">
                    <label htmlFor="itemType" className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      id="itemType"
                      name="type"
                      value={newItem.type}
                      onChange={handleNewItemChange}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                    >
                      <option value="deduction">Deduction</option>
                      <option value="benefit">Benefit</option>
                    </select>
                  </div>
                  
                  <div className="md:col-span-1">
                    <label htmlFor="itemName" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="itemName"
                      name="name"
                      value={newItem.name}
                      onChange={handleNewItemChange}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                      placeholder="e.g., Healthcare"
                    />
                  </div>
                  
                  <div className="md:col-span-1">
                    <label htmlFor="itemValueType" className="block text-sm font-medium text-gray-700 mb-1">
                      Value Type
                    </label>
                    <select
                      id="itemValueType"
                      name="valueType"
                      value={newItem.valueType}
                      onChange={handleNewItemChange}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>
                  
                  <div className="md:col-span-1">
                    <label htmlFor="itemValue" className="block text-sm font-medium text-gray-700 mb-1">
                      Value
                    </label>
                    <input
                      type="number"
                      id="itemValue"
                      name="value"
                      value={newItem.value}
                      onChange={handleNewItemChange}
                      min="0"
                      step="0.01"
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                      placeholder={newItem.valueType === 'percentage' ? '5%' : '$100'}
                    />
                  </div>
                  
                  <div className="md:col-span-1 flex items-end">
                    <button
                      type="button"
                      onClick={handleAddItem}
                      disabled={!newItem.name || !newItem.value}
                      className={`w-full px-4 py-2.5 rounded-lg ${
                        !newItem.name || !newItem.value
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-[#190192] text-white hover:bg-[#2C0DB5]'
                      }`}
                    >
                      Add
                    </button>
                  </div>
                </div>
                <div className="mt-2">
                  <input
                    type="text"
                    id="itemDescription"
                    name="description"
                    value={newItem.description}
                    onChange={handleNewItemChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                    placeholder="Description (optional)"
                  />
                </div>
              </div>
              
              {/* Display current deductions and benefits */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-2">Default Deductions</h3>
                  {config.defaultDeductions.length === 0 ? (
                    <p className="text-gray-500 text-sm">No default deductions added</p>
                  ) : (
                    <div className="space-y-2">
                      {config.defaultDeductions.map((deduction, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                        >
                          <div>
                            <div className="font-medium text-gray-900">{deduction.name}</div>
                            <div className="text-sm text-gray-500">
                              {deduction.type === 'percentage' ? `${deduction.value}%` : `$${deduction.value}`}
                              {deduction.description && ` - ${deduction.description}`}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem('deduction', index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-2">Default Benefits</h3>
                  {config.defaultBenefits.length === 0 ? (
                    <p className="text-gray-500 text-sm">No default benefits added</p>
                  ) : (
                    <div className="space-y-2">
                      {config.defaultBenefits.map((benefit, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                        >
                          <div>
                            <div className="font-medium text-gray-900">{benefit.name}</div>
                            <div className="text-sm text-gray-500">
                              {benefit.type === 'percentage' ? `${benefit.value}%` : `$${benefit.value}`}
                              {benefit.description && ` - ${benefit.description}`}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem('benefit', index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className={`px-6 py-2.5 bg-[#190192] text-white font-medium rounded-lg ${
                saving ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#2C0DB5]'
              } flex items-center`}
            >
              {saving ? (
                <>
                  <div className="animate-spin h-4 w-4 mr-2 border-2 border-white rounded-full border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollSettings;